<template>
  <div class="flex flex-col w-full" style="position: relative; width: 100%">
    <div class="flex !grow-0 items-center gap-4 pa-2" style="width: 100%; font-weight: bold; overflow: auto">
      <div class="cursor-pointer flex items-center gap-2" @click="showCoverageAreaTab()">
        <v-icon>mdi-select-group</v-icon>
        <span :class="{ selectedTab: isCoverageAreaTabVisible }">{{ $t('Simulation.Coverage') }}</span>
      </div>
      |
      <div class="cursor-pointer flex items-center gap-2" @click="showExcludeAreaTab()">
        <v-icon>mdi-selection-remove</v-icon>
        <span :class="{ selectedTab: isExcludeAreaTabVisible }">{{ $t('Simulation.ExcludeArea') }}</span>
      </div>
      |
      <div class="cursor-pointer flex items-center gap-2" @click="showConfigurationTab()">
        <v-icon>mdi-cog</v-icon>
        <span :class="{ selectedTab: isConfigurationTabVisible }">{{ $t('Simulation.Configuration') }}</span>
      </div>
      <v-spacer />
      <div class="flex items-center gap-1">
        <v-btn class="ma-0" icon small @click="bestFit()"><v-icon>mdi-target</v-icon></v-btn>
        <v-btn class="ma-0" v-if="isCoverageAreaTabVisible" icon small @click="renameWayPoint()">
          <v-icon>mdi-sort-alphabetical-descending-variant</v-icon>
        </v-btn>
        <v-btn class="ma-0" v-if="isCoverageAreaTabVisible" icon small @click="showGlobalWaypoint()">
          <v-icon>mdi-map-marker-plus</v-icon>
        </v-btn>
        <v-btn class="ma-0" v-if="isExcludeAreaTabVisible" icon small @click="addExcludeArea()">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
        <v-btn class="my-0" v-if="isCoverageAreaTabVisible" icon small @click="deleteCoverageArea()">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </div>
    </div>
    <div class="grow w-full" style="position: relative; background-color: white">
      <div class="mx-4 scrollable-container">
        <div v-if="isCoverageAreaTabVisible">
          <smart-coverage-wpt-list :areaId="-1" :coverageState="editCoverageState" :wptList="config.coverage_area" />
        </div>
        <div v-if="isExcludeAreaTabVisible">
          <smart-coverage-exclude-area
            v-for="excludeArea in config.exclude_areas"
            :key="excludeArea.id"
            @rename="renameWayPoint(excludeArea)"
            @delete="deleteArea(excludeArea.id)"
            :excludeArea="excludeArea"
            :coverageState="editCoverageState"
          />
        </div>
        <div v-if="isConfigurationTabVisible">
          <h3>{{ $t('Simulation.GridParameter') }}</h3>
          <div class="flex items-center gap-4">
            <div style="width: 155px; height: 20px" class="pb-4 !grow-0 flex justify-end shrink-0">
              {{ $t('Simulation.GridSize') }} :
            </div>
            <number-field-factor
              v-model.number="config.grid_size"
              :hint="$t('Simulation.GridSizedHint')"
              persistent-hint
              :min="1"
              :max="50"
            />
          </div>
          <div class="flex items-end gap-4">
            <div style="width: 155px; height: 20px" class="pb-4 !grow-0 flex justify-end shrink-0">
              {{ $t('Simulation.DeploymentMode') }} :
            </div>
            <v-select
              hide-details
              :items="deprecoModes"
              item-text="libelle"
              item-value="value"
              v-model="config.deployment_mode"
            ></v-select>
          </div>
          <div class="flex items-end gap-4" v-if="config.deployment_mode == 'common'">
            <div style="width: 155px; height: 20px" class="pb-4 !grow-0 flex justify-end shrink-0">
              {{ $t('Simulation.WptDeployment') }} :
            </div>
            <v-select
              hide-details
              :items="gridPointList"
              item-text="libelle"
              item-value="id"
              v-model.number="config.deployment_point"
            ></v-select>
            <v-btn class="ma-0" icon small @click="selectDeployment()"><v-icon>mdi-map-marker-down</v-icon></v-btn>
          </div>
          <div class="flex items-end gap-4">
            <div style="width: 155px; height: 20px" class="pb-4 !grow-0 flex justify-end shrink-0">
              {{ $t('Simulation.RecoveryMode') }} :
            </div>
            <v-select
              hide-details
              :items="deprecoModes"
              item-text="libelle"
              item-value="value"
              v-model="config.recovery_mode"
            ></v-select>
          </div>
          <div class="flex items-end gap-4" v-if="config.recovery_mode == 'common'">
            <div style="width: 155px; height: 20px" class="pb-4 !grow-0 flex justify-end shrink-0">
              {{ $t('Simulation.WptRecovery') }} :
            </div>
            <v-select
              hide-details
              :items="gridPointList"
              item-text="libelle"
              item-value="id"
              v-model.number="config.recovery_point"
            ></v-select>
            <v-btn class="ma-0" icon small @click="selectRecovery()">
              <v-icon>mdi-map-marker-remove-variant</v-icon>
            </v-btn>
          </div>
          <SimulationDetailParameter covMode />
        </div>
      </div>
    </div>
    <div class="flex shrink-0 !grow-0 w-full mt-auto">
      <v-btn :disabled="!isRunnable" class="!grow-0" @click="runGridCoverageCalculation()" small color="primary">
        {{ $t('Simulation.RunCoverageGrid') }}
        <v-progress-circular
          class="ml-2"
          :size="20"
          v-if="isGridRunning"
          indeterminate
          color="white"
        ></v-progress-circular>
        <v-icon v-else>mdi-dots-grid</v-icon>
      </v-btn>
      <v-btn :disabled="!isRunnable" class="!grow-0" @click="runCoverageSimulation()" small color="primary">
        {{ $t('Simulation.Run') }}
        <v-progress-circular
          class="ml-2"
          :size="20"
          v-if="isCoverageRunning"
          indeterminate
          color="white"
        ></v-progress-circular>
        <v-icon v-else>mdi-play</v-icon>
      </v-btn>
      <v-btn :disabled="!btnActif" @click="saveConfig()" small color="primary" class="ml-auto !grow-0">
        {{ $t('Pilot.MissionPlan.PublishStagingToIris') }}
        <v-icon small right>mdi-content-save</v-icon>
      </v-btn>
    </div>
    <admin-mission-plan-global-wpt-dialog @addWaypoint="addWaypoint" v-model="editCoverageState.showGlobalWpts" />
  </div>
</template>
<script lang="ts">
import { MissionPlanUiState, buildMissionPlanStateId } from '@/models/ui/MissionPlanUiState'
import AdminMissionPlan from '@/components/admin/missionPlanTemplate/AdminMissionPlan.vue'
import AdminMissionPlanGlobalWptDialog from '@/components/admin/missionPlanTemplate/AdminMissionPlanGlobalWptDialog.vue'
import SmartCoverageWptList from '@/components/smart/Coverage/WptList.vue'
import SmartCoverageExcludeArea from '@/components/smart/Coverage/ExcludeArea.vue'
import SimulationDetailParameter from '@/components/smart/SimulationDetailParameter.vue'

import Vue from 'vue'
import Component from 'vue-class-component'
import deepEqual from 'deep-equal'
import { Watch } from 'vue-property-decorator'
import { cloneDeep } from 'lodash'
import { buildSimulationUIStateId } from '@/models/ui/SimulationUiState'
import { MapUiState } from '@/models/ui/MapUiState'
import { DepRecoMode } from '@/enum/DepRecoMode'
import { buildCoverageStateId } from '@/models/ui/CoverageUiState'
import { IMissionPlanWaypoint } from '@/models/iris/missionPlan/IMissionPlanWaypoint'
import {
  ISmartSimulationCoverage,
  isReadyForSimulationCoverage,
} from '@/models/iris/smartMission/ISmartSimulationCoverage'
import { getDefaultArea, IArea } from '@/models/iris/smartMission/IArea'
import apiMissionGlobal from '@/api/admin/missionGlobal'
import { VuetifyConfirmObject } from 'vuetify-confirm/types'
import { AloDbMissionGlobalCoverage } from '@/models/alodb/AloDbMissionGlobal'

@Component({
  name: 'CoverageDetailParameter',
  components: {
    AdminMissionPlan,
    AdminMissionPlanGlobalWptDialog,
    SmartCoverageWptList,
    SmartCoverageExcludeArea,
    SimulationDetailParameter,
  },
})
export default class CoverageDetailParameter extends Vue {
  visibleTab = 'coverage' //coverage || exclude || configuration
  get isCoverageAreaTabVisible() {
    return this.visibleTab == 'coverage'
  }

  get isExcludeAreaTabVisible() {
    return this.visibleTab == 'exclude'
  }

  get isConfigurationTabVisible() {
    return this.visibleTab == 'configuration'
  }

  showCoverageAreaTab() {
    this.editCoverageState!.editAreaId = -1
    this.visibleTab = 'coverage'
  }

  showExcludeAreaTab() {
    this.visibleTab = 'exclude'
  }

  showConfigurationTab() {
    this.visibleTab = 'configuration'
  }

  showGlobalWaypoint() {
    this.editCoverageState!.editAreaId = -1
    this.editCoverageState!.showGlobalWpts = true
  }

  deleteCoverageArea() {
    this.$confirm(this.$t('Simulation.ConfirmDeleteAreaMessage').toString()).then(remove => {
      if (remove) {
        this.config.coverage_area = []
      }
    })
  }

  renameWayPoint(area?: IArea) {
    const prefixe = area ? `e${area.id}` : ''
    ;(area?.area_pts || this.config.coverage_area).forEach((pt, idx) => {
      const nameChar = Array.from(idx.toString(26))
      pt.wpt_label = nameChar.reduce(
        (name, char, idx) =>
          `${prefixe}${name}${this.getLetter(Number.parseInt(char, 26) - (idx < nameChar.length - 1 ? 1 : 0))}`,
        '',
      )
    })
  }

  //recupere la lettre de l'alphabet en majuscule en fonction d'un index commencant à 0
  getLetter(index: number) {
    return String.fromCharCode(65 + index)
  }

  addExcludeArea() {
    const nextAreaId = this.nextAreaId()
    this.config.exclude_areas.push(getDefaultArea({ id: nextAreaId }))
  }

  deleteArea(id: number | null) {
    this.editCoverageState!.editAreaId = -1
    this.config.exclude_areas.splice(
      this.config.exclude_areas.findIndex(i => i.id == id),
      1,
    )
  }

  bestFit() {
    this.$store.myActions.UI_REFRESH_BEST_FIT_MAP(this.map_uiid)
  }

  selectRecovery() {
    //@ts-ignore
    this.editCoverageState.drawMode = 'RECOVERY'
  }

  selectDeployment() {
    //@ts-ignore
    this.editCoverageState.drawMode = 'DEPLOYMENT'
  }

  nextAreaId() {
    return this.config.exclude_areas?.length ? Math.max(...this.config.exclude_areas.map(i => i.id!)) + 1 : 1
  }

  nextWptsId() {
    return this.wptList?.length ? Math.max(...this.wptList.map(i => i.id)) + 1 : 1
  }

  addWaypoint({ wpt, edit }: { wpt: IMissionPlanWaypoint; edit: boolean }) {
    if (edit) {
      wpt.wpt_lat =
        this.wptList.length == 0
          ? 0
          : this.wptList.reduce((acc, value) => {
              return acc + value.wpt_lat
            }, 0) / this.wptList.length
      wpt.wpt_lon =
        this.wptList.length == 0
          ? 0
          : this.wptList.reduce((acc, value) => {
              return acc + value.wpt_lon
            }, 0) / this.wptList.length
    }
    const newWpt = { ...wpt, id: this.nextWptsId() } as IMissionPlanWaypoint
    this.wptList.push(newWpt)
    if (edit) {
      this.editCoverageState!.currentEditWpt = JSON.parse(JSON.stringify(newWpt))
    }
  }

  get editCoverageState() {
    return this.editCoverageStateUiid ? this.$store.myGetters.coverageStateByUiid(this.editCoverageStateUiid) : null
  }

  get editCoverageStateUiid() {
    return buildCoverageStateId(this.modified.MIG_ID || -1, 'detail')
  }

  get deprecoModes() {
    return [
      { libelle: this.$t('Simulation.Common'), value: DepRecoMode.common },
      { libelle: this.$t('Simulation.Optimal'), value: DepRecoMode.optimal },
    ]
  }

  get gridPointList() {
    return (
      this.modified.resultGridJson?.wpts?.map(w => ({
        //@ts-ignore
        libelle: `N°${w.wpt_label}  (${this.$options.filters[this.$store.myGetters.coordFormatFilter]([
          w.wpt_lon,
          w.wpt_lat,
        ])})`,
        id: w.id,
      })) ?? []
    )
  }

  get modified() {
    return this.$store.myGetters.modifiedAdmMissionGlobal as AloDbMissionGlobalCoverage
  }

  get original() {
    return this.$store.myGetters.originalAdmMissionGlobal as AloDbMissionGlobalCoverage
  }

  get wptList() {
    return (
      (this.editCoverageState?.editAreaId ?? -1) > 0
        ? this.config.exclude_areas.find(a => a.id == this.editCoverageState!.editAreaId!)?.area_pts
        : this.config.coverage_area
    ) as Array<IMissionPlanWaypoint>
  }

  get config() {
    return this.modified.config as ISmartSimulationCoverage
  }

  get btnActif() {
    return !deepEqual(this.original, this.modified) && !this.editCoverageState!.saving
  }

  created() {}

  get uistatebyuiid() {
    return this.$store.myGetters.uistatebyuiid
  }

  get mapState() {
    return this.uistatebyuiid(this.map_uiid) as MapUiState
  }

  get bestFitMisId() {
    return this.mapState?.bestFitMisId
  }

  get missionVirtualsById() {
    return this.$store.myGetters.missionVirtualsById
  }

  get selectedMission() {
    return this.missionVirtualsById[this.bestFitMisId]
  }

  get isGridRunning() {
    return this.editCoverageState?.runningGrid
  }

  get isCoverageRunning() {
    return this.editCoverageState?.runningCoverage
  }

  get isRunnable() {
    //si toute les simulations ont un status différent de running
    return !this.isGridRunning && !this.isCoverageRunning
  }

  get missionPlanUiid() {
    return buildMissionPlanStateId(this.modified.MIG_ID || -1, 'simulation')
  }

  get missionPlanUiState() {
    return this.$store.myGetters.uistatebyuiid(this.missionPlanUiid) as MissionPlanUiState
  }

  get isOperator() {
    return this.$store.myGetters.isOperator
  }

  get isSysAdmin() {
    return this.$store.myGetters.isSysAdmin
  }

  get map_uiid() {
    return buildSimulationUIStateId(this.original.MIG_ID!, 'map')
  }

  async saveConfig(executeNext = async () => {}) {
    this.editCoverageState!.drawMode = ''
    this.editCoverageState!.currentEditWpt = MissionPlanUiState.getDefaultWayPoint()
    this.editCoverageState!.hoverWptKey = ''
    this.$store.myActions.UI_SET_MEASURETOOL('NONE')

    this.editCoverageState!.saving = true
    this.$nextTick(async () => {
      await this.$store.myActions.MISSIONGLOBAL_SAVE_REQUEST()
      this.editCoverageState!.selectedCoverageId = null
      await executeNext()
      this.editCoverageState!.saving = false
    })
  }

  runGridCoverageCalculation() {
    this.editCoverageState!.runningGrid = true
    this.saveConfig(this.doRunGridCoverageCalculation)
  }

  runCoverageSimulation() {
    this.editCoverageState!.runningCoverage = true
    this.saveConfig(this.doRunCoverageSimulation)
  }

  async doRunGridCoverageCalculation() {
    const errors = isReadyForSimulationCoverage(this.original.config!)
    //console.log(errors)
    if (!errors?.length) {
      this.editCoverageState!.runningGrid = true
      try {
        await this.$store.myActions.MISSIONGLOBAL_RUN_COVERAGE_GRID_REQUEST()
      } finally {
        this.editCoverageState!.runningGrid = false
      }
    } else {
      this.editCoverageState!.runningGrid = false
      this.$confirm(`<ul><li>${errors.map(e => this.$t(`Simulation.Check.${e}`)).join('</li><li>')}</li></ul>`, {
        title: this.$t('Simulation.Check.Title'),
        buttonTrueText: 'Ok',
        buttonFalseText: '',
      } as VuetifyConfirmObject)
    }
  }

  async doRunCoverageSimulation() {
    this.editCoverageState!.runningCoverage = true
    await this.doRunGridCoverageCalculation()
    try {
      await this.$store.myActions.MISSIONGLOBAL_RUN_COVERAGE_SIMULATION_REQUEST()
    } finally {
      this.editCoverageState!.runningCoverage = false
    }
  }
}
</script>
